import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { usePostHog } from 'posthog-js/react';
import { IconArrowLeft, IconCamera, IconTrophy, IconCheck, IconUser } from '@tabler/icons-react';
import { auth, db, functions, storage } from '../firebase';
import Footer from '../components/Footer';
import { ref, uploadBytes, getDownloadURL } from "firebase/storage";
import { doc, getDoc, updateDoc } from "firebase/firestore";
import { httpsCallable } from "firebase/functions";
import { signOut } from "firebase/auth";
import AutoUploadSettings from '../components/AutoUploadSettings';
import ChallengeProgressUploadSettings from '../components/ChallengeProgressUploadSettings';
import AccountEmailRow from '../components/AccountEmailRow';
import './SettingsPage.css';

// Strava OAuth Configuration
const STRAVA_CLIENT_ID = "159733";
const STRAVA_SIGNIN_REDIRECT_URI = window.location.origin + "/strava-auth-callback";
const STRAVA_SCOPE = "activity:read,activity:read_all,activity:write,profile:read_all";

function SettingsPage({ user }) {
  const navigate = useNavigate();
  const { t, i18n } = useTranslation();
  const posthog = usePostHog();
  const [stravaConnected, setStravaConnected] = useState(false);
  const [isCheckingStrava, setIsCheckingStrava] = useState(true);
  const [newChallengeEmails, setNewChallengeEmails] = useState(true);
  const [monthlyRecapEmails, setMonthlyRecapEmails] = useState(true);
  // null = unknown or no email on the account (toggle hidden)
  const [newsletterSubscribed, setNewsletterSubscribed] = useState(null);
  const [accountEmail, setAccountEmail] = useState(null);
  // false = the getEmailSettings callable is unavailable (not deployed yet),
  // so the section renders exactly the legacy Email Notifications block.
  const [emailSettingsAvailable, setEmailSettingsAvailable] = useState(false);
  const [northUpMaps, setNorthUpMaps] = useState(false);
  const [isLoadingSettings, setIsLoadingSettings] = useState(true);

  // Challenge profile state
  const [challengeDisplayName, setChallengeDisplayName] = useState('');
  const [challengePhotoPreview, setChallengePhotoPreview] = useState(null);
  const [challengePhotoFile, setChallengePhotoFile] = useState(null);
  const [isSavingChallengeProfile, setIsSavingChallengeProfile] = useState(false);
  const [challengeProfileSaved, setChallengeProfileSaved] = useState(false);

  // Account deletion state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [isDeletingAccount, setIsDeletingAccount] = useState(false);

  // Strava disconnect confirmation state
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);
  const [isDisconnectingStrava, setIsDisconnectingStrava] = useState(false);
  const [disconnectError, setDisconnectError] = useState(null);

  useEffect(() => {
    if (user) {
      checkStravaConnection(user.uid);
      loadUserSettings(user.uid);
    } else {
      setStravaConnected(false);
      setIsCheckingStrava(false);
      setIsLoadingSettings(false);
    }
  }, [user]);

  const checkStravaConnection = async (userId) => {
    setIsCheckingStrava(true);
    try {
      const tokenDocRef = doc(db, 'users', userId, 'stravaTokens', 'tokens');
      const docSnap = await getDoc(tokenDocRef);
      setStravaConnected(docSnap.exists());
    } catch (error) {
      console.error("Error checking Strava connection:", error);
      setStravaConnected(false);
    } finally {
      setIsCheckingStrava(false);
    }
  };

  const loadUserSettings = async (userId) => {
    setIsLoadingSettings(true);
    try {
      const userDocRef = doc(db, 'users', userId);
      const docSnap = await getDoc(userDocRef);
      if (docSnap.exists()) {
        const userData = docSnap.data();

        // Apply any previously-saved language preference. The picker has been
        // removed (English is the default), but existing users keep their choice.
        const savedLanguage = userData?.settings?.language;
        if (savedLanguage && savedLanguage !== i18n.language && (savedLanguage === 'en' || savedLanguage === 'zh-CN')) {
          i18n.changeLanguage(savedLanguage);
        }

        // Load email preferences (under settings, default: true)
        setNewChallengeEmails(userData?.settings?.newChallengeEmails !== false);
        setMonthlyRecapEmails(userData?.settings?.monthlyRecapEmails !== false);

        // Map bearing mode (default: auto search when absent)
        setNorthUpMaps(userData?.settings?.mapBearingMode === 'north_up');

        // Email settings (address + newsletter state) via the new callable,
        // falling back to the legacy newsletter-only lookup when it isn't
        // deployed yet so a hosting-first deploy renders today's section.
        try {
          const getEmailSettingsFunc = httpsCallable(functions, 'getEmailSettings');
          const emailResult = await getEmailSettingsFunc();
          setEmailSettingsAvailable(true);
          setAccountEmail(typeof emailResult?.data?.email === 'string' ? emailResult.data.email : null);
          setNewsletterSubscribed(
            typeof emailResult?.data?.newsletterSubscribed === 'boolean' ? emailResult.data.newsletterSubscribed : null
          );
        } catch (emailError) {
          if (emailError?.code !== 'functions/not-found') {
            console.warn('Could not load email settings:', emailError);
          }
          // Newsletter subscription lives in the email suppression list, not the
          // user doc; best-effort — the toggle stays hidden if this fails.
          try {
            const getNewsletterSubscriptionFunc = httpsCallable(functions, 'getNewsletterSubscription');
            const subResult = await getNewsletterSubscriptionFunc();
            setNewsletterSubscribed(
              typeof subResult?.data?.subscribed === 'boolean' ? subResult.data.subscribed : null
            );
          } catch (subError) {
            console.warn('Could not load newsletter subscription state:', subError);
          }
        }

        // Load challenge profile fields
        setChallengeDisplayName(userData?.challengeDisplayName || userData?.displayName || user.displayName || '');
        setChallengePhotoPreview(userData?.challengePhotoURL || userData?.photoURL || user.photoURL || null);
      }
    } catch (error) {
      console.error("Error loading user settings:", error);
    } finally {
      setIsLoadingSettings(false);
    }
  };

  const handleSignOut = async () => {
    try {
      await signOut(auth);
      navigate('/');
    } catch (error) {
      console.error("Error signing out:", error);
      alert("Failed to sign out. Please try again.");
    }
  };

  const handleBack = () => {
    navigate(-1);
  };

  const updateChallengeEmailPref = async (field, newValue, setter) => {
    if (!user) return;
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, { [`settings.${field}`]: newValue });
      setter(newValue);
    } catch (error) {
      console.error(`Error updating ${field}:`, error);
      alert("Failed to update setting. Please try again.");
    }
  };

  const updateNewsletterSubscription = async (nextSubscribed) => {
    if (!user) return;
    const previous = newsletterSubscribed;
    setNewsletterSubscribed(nextSubscribed);
    try {
      const setNewsletterSubscriptionFunc = httpsCallable(functions, 'setNewsletterSubscription');
      await setNewsletterSubscriptionFunc({ subscribed: nextSubscribed });
    } catch (error) {
      console.error('Error updating newsletter subscription:', error);
      setNewsletterSubscribed(previous);
      alert("Failed to update setting. Please try again.");
    }
  };

  const handleEmailSettingsChanged = (data) => {
    if (typeof data?.email === 'string') setAccountEmail(data.email);
    if (typeof data?.newsletterSubscribed === 'boolean') setNewsletterSubscribed(data.newsletterSubscribed);
    posthog?.capture('setting_changed', { setting_name: 'account_email' });
  };

  const updateNorthUpMaps = async (nextNorthUp) => {
    if (!user) return;
    const previous = northUpMaps;
    setNorthUpMaps(nextNorthUp);
    try {
      const userDocRef = doc(db, 'users', user.uid);
      await updateDoc(userDocRef, {
        'settings.mapBearingMode': nextNorthUp ? 'north_up' : 'auto_search'
      });
    } catch (error) {
      console.error('Error updating map bearing mode:', error);
      setNorthUpMaps(previous);
      alert("Failed to update setting. Please try again.");
    }
  };

  // Unsubscribe links in emails point at /settings#email-notifications.
  useEffect(() => {
    if (isLoadingSettings) return;
    if (typeof window === 'undefined') return;
    if (window.location.hash !== '#email-notifications') return;
    const el = document.getElementById('email-notifications');
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [isLoadingSettings]);

  const handleStravaConnect = () => {
    if (!STRAVA_CLIENT_ID || STRAVA_CLIENT_ID === "YOUR_STRAVA_CLIENT_ID") {
      alert("Strava Client ID placeholder is still in use. Please update it.");
      return;
    }

    const state = Math.random().toString(36).substring(2);
    localStorage.setItem('stravaOAuthState', state);

    const consentUrl = `https://www.strava.com/oauth/authorize?client_id=${STRAVA_CLIENT_ID}&redirect_uri=${encodeURIComponent(STRAVA_SIGNIN_REDIRECT_URI)}&response_type=code&scope=${encodeURIComponent(STRAVA_SCOPE)}&approval_prompt=auto&state=${state}`;
    window.location.href = consentUrl;
  };

  const handleStravaDisconnect = () => {
    if (!user) return;
    setDisconnectError(null);
    setShowDisconnectModal(true);
  };

  const confirmStravaDisconnect = async () => {
    setIsDisconnectingStrava(true);
    setDisconnectError(null);
    try {
      const disconnectStravaFunc = httpsCallable(functions, 'disconnectStrava');
      await disconnectStravaFunc();
      setStravaConnected(false);
      setShowDisconnectModal(false);
    } catch (error) {
      console.error("Error disconnecting Strava:", error);
      setDisconnectError(
        error.message
          ? t('settingsPage.disconnectStravaError', { defaultValue: 'Failed to disconnect Strava: {{message}}', message: error.message })
          : t('settingsPage.disconnectStravaErrorGeneric', 'Failed to disconnect Strava. Please try again.')
      );
    } finally {
      setIsDisconnectingStrava(false);
    }
  };

  // Challenge profile handlers
  const handleChallengePhotoSelect = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setChallengePhotoFile(file);
    const reader = new FileReader();
    reader.onload = (ev) => setChallengePhotoPreview(ev.target.result);
    reader.readAsDataURL(file);
  };

  const handleSaveChallengeProfile = async () => {
    if (!user) return;
    setIsSavingChallengeProfile(true);
    setChallengeProfileSaved(false);
    try {
      let photoURL = null;

      // Upload new photo if selected
      if (challengePhotoFile) {
        const photoRef = ref(storage, `users/${user.uid}/challenge-profile.jpg`);
        await uploadBytes(photoRef, challengePhotoFile);
        photoURL = await getDownloadURL(photoRef);
        setChallengePhotoPreview(photoURL);
        setChallengePhotoFile(null);
      }

      // Call backend to update user doc + all participant docs
      const updateFunc = httpsCallable(functions, 'updateChallengeProfile');
      const payload = {};
      if (challengeDisplayName.trim()) {
        payload.displayName = challengeDisplayName.trim();
      }
      if (photoURL) {
        payload.photoURL = photoURL;
      }
      await updateFunc(payload);

      setChallengeProfileSaved(true);
      setTimeout(() => setChallengeProfileSaved(false), 3000);

      posthog?.capture('setting_changed', { setting_name: 'challenge_profile' });
    } catch (error) {
      console.error('Error saving challenge profile:', error);
      alert('Failed to update challenge profile. Please try again.');
    } finally {
      setIsSavingChallengeProfile(false);
    }
  };

  const handleDeleteAccount = async () => {
    if (deleteConfirmText !== 'DELETE') return;

    setIsDeletingAccount(true);

    try {
      const deleteUserAccountFunc = httpsCallable(functions, 'deleteUserAccount');
      const result = await deleteUserAccountFunc();

      if (result.data.success) {
        // Track account deletion
        posthog?.capture('account_deleted', {
          generations_deleted: result.data.summary?.generationsDeleted || 0,
          files_deleted: result.data.summary?.storageFilesDeleted || 0
        });

        // Sign out and redirect to home
        await signOut(auth);
        navigate('/');
      }
    } catch (error) {
      console.error("Error deleting account:", error);
      alert("Failed to delete account. Please try again or contact support.");
    } finally {
      setIsDeletingAccount(false);
      setShowDeleteModal(false);
      setDeleteConfirmText('');
    }
  };

  // Listen for Strava auth success messages
  useEffect(() => {
    const handleStravaSuccess = (event) => {
      if (event.origin !== window.location.origin || event.data !== "stravaAuthSuccess") return;
      if (user) checkStravaConnection(user.uid);
    };
    window.addEventListener("message", handleStravaSuccess);
    return () => window.removeEventListener("message", handleStravaSuccess);
  }, [user]);

  if (!user) {
    return (
      <div className="settings-page">
        <div className="settings-not-logged-in">
          <h2>{t('settingsPage.signInRequired', 'Sign in required')}</h2>
          <p>{t('settingsPage.signInToAccess', 'Please sign in to access your settings.')}</p>
        </div>
      </div>
    );
  }

  return (
    <div className="settings-page">
      <div className="settings-container">
        <div className="settings-header">
          <button className="back-button" onClick={handleBack}>
            <IconArrowLeft size={20} />
            {t('settingsPage.back', 'Back')}
          </button>
          <h1>{t('settingsPage.title', 'Settings')}</h1>
          <p className="settings-subtitle">{t('settingsPage.subtitle', 'Manage your account and preferences')}</p>
        </div>
        <div className="settings-divider"></div>

        {/* Sections 1-3: Indoor Cycling Management, Maps auto-upload settings,
            and the shared upload format & units. AutoUploadSettings renders all
            three; they require Strava. */}
        {stravaConnected && <AutoUploadSettings user={user} />}

        {/* Challenges (profile + Strava auto-append + email prefs grouped together) */}
        {stravaConnected && (
          <div className="settings-section">
            <h2>
              <IconTrophy size={24} />
              Challenges
            </h2>
            <p className="section-description">Your challenge profile, Strava sharing, and email preferences for challenge activity.</p>

            {/* Challenge profile */}
            <div className="preference-card">
              <div className="challenge-profile-editor">
                <div className="challenge-avatar-wrapper">
                  <img
                    src={challengePhotoPreview || user?.photoURL || '/default-avatar.png'}
                    alt="Challenge profile"
                    className="challenge-avatar-img"
                  />
                  <label className="challenge-avatar-upload">
                    <IconCamera size={14} />
                    <input type="file" accept="image/*" onChange={handleChallengePhotoSelect} hidden />
                  </label>
                </div>

                <div className="challenge-profile-fields">
                  <div className="preference-item">
                    <div className="preference-info">
                      <h4>Challenge Display Name</h4>
                      <p>Shown on leaderboards and challenge pages</p>
                    </div>
                  </div>
                  <input
                    type="text"
                    value={challengeDisplayName}
                    onChange={(e) => setChallengeDisplayName(e.target.value)}
                    placeholder="Your challenge name"
                    className="challenge-name-input"
                    disabled={isLoadingSettings}
                  />
                </div>
              </div>

              {challengeProfileSaved && (
                <div className="challenge-profile-saved">
                  <IconCheck size={16} /> Challenge profile updated!
                </div>
              )}

              <button
                onClick={handleSaveChallengeProfile}
                disabled={isSavingChallengeProfile || isLoadingSettings}
                className="save-challenge-profile-btn"
              >
                {isSavingChallengeProfile ? 'Saving...' : 'Save Challenge Profile'}
              </button>

              <div className="settings-row-divider" />

              {/* New challenge announcements */}
              <div className="preference-item">
                <div className="preference-info">
                  <h4>{t('settingsPage.newChallengeAnnouncements', 'New challenge announcements')}</h4>
                  <p>{t('settingsPage.newChallengeAnnouncementsDesc', 'Get notified when a new challenge is available to join')}</p>
                </div>
                <div className="preference-control">
                  {isLoadingSettings ? (
                    <div className="loading-spinner-small"></div>
                  ) : (
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={newChallengeEmails}
                        onChange={(e) => updateChallengeEmailPref('newChallengeEmails', e.target.checked, setNewChallengeEmails)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  )}
                </div>
              </div>

              <div className="settings-row-divider" />

              {/* Challenge progress on Strava */}
              <ChallengeProgressUploadSettings user={user} />
            </div>
          </div>
        )}

        {/* Email section (anchor target for email unsubscribe links). Falls
            back to the legacy notifications-only block until the
            getEmailSettings callable is deployed. */}
        <div className="settings-section" id="email-notifications">
          <h2>
            {emailSettingsAvailable ?
              t('settingsPage.emailSection', 'Email') :
              t('settingsPage.emailNotifications', 'Email Notifications')}
          </h2>
          <p className="section-description">
            {emailSettingsAvailable ?
              t('settingsPage.emailSectionDesc', 'Your email address and which emails you receive from Mythic Maps') :
              t('settingsPage.emailNotificationsDesc', 'Choose which emails you receive from Mythic Maps')}
          </p>
          <div className="preference-card">
            {emailSettingsAvailable && !isLoadingSettings && (
              <>
                <AccountEmailRow email={accountEmail} onChanged={handleEmailSettingsChanged} />
                <div className="settings-row-divider" />
              </>
            )}
            <div className="preference-item">
              <div className="preference-info">
                <h4>{t('settingsPage.monthlyRecapEmail', 'Monthly recap email')}</h4>
                <p>{t('settingsPage.monthlyRecapEmailDesc', 'A monthly summary of your maps, stats, and highlights')}</p>
              </div>
              <div className="preference-control">
                {isLoadingSettings ? (
                  <div className="loading-spinner-small"></div>
                ) : (
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={monthlyRecapEmails}
                      onChange={(e) => updateChallengeEmailPref('monthlyRecapEmails', e.target.checked, setMonthlyRecapEmails)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                )}
              </div>
            </div>

            {newsletterSubscribed !== null && (
              <>
                <div className="settings-row-divider" />
                <div className="preference-item">
                  <div className="preference-info">
                    <h4>{t('settingsPage.newsletterEmails', 'Product news & updates')}</h4>
                    <p>{t('settingsPage.newsletterEmailsDesc', 'Occasional announcements and the Mythic Maps newsletter')}</p>
                  </div>
                  <div className="preference-control">
                    <label className="toggle-switch">
                      <input
                        type="checkbox"
                        checked={newsletterSubscribed}
                        onChange={(e) => updateNewsletterSubscription(e.target.checked)}
                      />
                      <span className="toggle-slider"></span>
                    </label>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Map preferences (applies to auto-uploads and maps created on /create) */}
        <div className="settings-section" id="map-preferences">
          <h2>{t('settingsPage.mapPreferences', 'Map Preferences')}</h2>
          <p className="section-description">{t('settingsPage.mapPreferencesDesc', 'Applies to all your maps — auto-uploads and maps you create yourself')}</p>
          <div className="preference-card">
            <div className="preference-item">
              <div className="preference-info">
                <h4>{t('settingsPage.northUpMaps', 'North-up maps')}</h4>
                <p>
                  {t(
                    'settingsPage.northUpMapsDesc',
                    'By default, auto search rotates the map to find the angle where your route best fills the frame. Turn this on to always keep north at the top instead. Zwift rides always use auto search.'
                  )}
                </p>
              </div>
              <div className="preference-control">
                {isLoadingSettings ? (
                  <div className="loading-spinner-small"></div>
                ) : (
                  <label className="toggle-switch">
                    <input
                      type="checkbox"
                      checked={northUpMaps}
                      onChange={(e) => updateNorthUpMaps(e.target.checked)}
                    />
                    <span className="toggle-slider"></span>
                  </label>
                )}
              </div>
            </div>
          </div>
        </div>

        {/* Section 4: Account (Strava connection, sign out, delete) */}
        <div className="settings-section">
          <h2>
            <IconUser size={24} style={{ color: '#FC5200' }} />
            {t('settingsPage.account', 'Account')}
          </h2>
          <p className="section-description">Your Strava connection, sign out, and account deletion.</p>

          <div className="preference-card">
            {/* Strava connection */}
            <div className="preference-item">
              <div className="preference-info">
                <h4>{t('settingsPage.stravaConnection', 'Strava connection')}</h4>
                <p>
                  {isCheckingStrava
                    ? t('settingsPage.checkingConnection', 'Checking connection...')
                    : (stravaConnected
                      ? t('settingsPage.connectedToStrava', 'Connected to Strava')
                      : t('settingsPage.notConnectedToStrava', 'Not connected to Strava'))}
                </p>
              </div>
              <div className="preference-control">
                {isCheckingStrava ? (
                  <div className="loading-spinner-small"></div>
                ) : !stravaConnected ? (
                  <button onClick={handleStravaConnect} className="strava-connect-button">
                    {t('settingsPage.connectWithStrava', 'Connect with Strava')}
                  </button>
                ) : (
                  <button onClick={handleStravaDisconnect} className="strava-disconnect-button">
                    {t('settingsPage.disconnectStrava', 'Disconnect Strava')}
                  </button>
                )}
              </div>
            </div>

            <div className="settings-row-divider" />

            {/* Sign out */}
            <div className="preference-item">
              <div className="preference-info">
                <h4>{t('settingsPage.signOut', 'Sign Out')}</h4>
                <p>{t('settingsPage.signOutDesc', 'Sign out of your Mythic Maps account')}</p>
              </div>
              <div className="preference-control">
                <button onClick={handleSignOut} className="signout-button">
                  {t('settingsPage.signOut', 'Sign Out')}
                </button>
              </div>
            </div>

            <div className="settings-row-divider" />

            {/* Delete account */}
            <div className="preference-item">
              <div className="preference-info">
                <h4 className="settings-danger-title">{t('settingsPage.deleteAccount', 'Delete Account')}</h4>
                <p>{t('settingsPage.deleteAccountDesc', 'Permanently delete your Mythic Maps account and all associated data. This action cannot be undone.')}</p>
              </div>
              <div className="preference-control">
                <button onClick={() => setShowDeleteModal(true)} className="danger-button">
                  {t('settingsPage.deleteMyAccount', 'Delete My Account')}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Section 5: Community */}
        <div className="settings-section">
          <h2>{t('settingsPage.communityAndSocial', 'Community & Social')}</h2>
          <p className="section-description">{t('settingsPage.communityDesc', 'Follow us and connect with the Mythic Maps community')}</p>

          <div className="community-grid">
            {/* YouTube */}
            <a href="https://www.youtube.com/channel/UCGBBpbNUx48ZtuG4V_c1CFQ" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon youtube">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF0000">
                  <path d="M23.498 6.186a3.016 3.016 0 0 0-2.122-2.136C19.505 3.545 12 3.545 12 3.545s-7.505 0-9.377.505A3.017 3.017 0 0 0 .502 6.186C0 8.07 0 12 0 12s0 3.93.502 5.814a3.016 3.016 0 0 0 2.122 2.136c1.871.505 9.376.505 9.376.505s7.505 0 9.377-.505a3.015 3.015 0 0 0 2.122-2.136C24 15.93 24 12 24 12s0-3.93-.502-5.814zM9.545 15.568V8.432L15.818 12l-6.273 3.568z"/>
                </svg>
              </div>
              <span>{t('settingsPage.youtube', 'YouTube')}</span>
            </a>

            {/* TikTok */}
            <a href="https://www.tiktok.com/@mythicmaps.ai" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon tiktok">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#000000">
                  <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5 20.1a6.34 6.34 0 0 0 10.86-4.43v-7a8.16 8.16 0 0 0 4.77 1.52v-3.4a4.85 4.85 0 0 1-1-.1z"/>
                </svg>
              </div>
              <span>{t('settingsPage.tiktok', 'TikTok')}</span>
            </a>

            {/* Instagram */}
            <a href="https://www.instagram.com/mythicmaps.ai/" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon instagram">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="url(#instagram-gradient)">
                  <defs>
                    <linearGradient id="instagram-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                      <stop offset="0%" style={{ stopColor: '#833AB4' }} />
                      <stop offset="50%" style={{ stopColor: '#E1306C' }} />
                      <stop offset="100%" style={{ stopColor: '#FD1D1D' }} />
                    </linearGradient>
                  </defs>
                  <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 1 0 0 12.324 6.162 6.162 0 0 0 0-12.324zM12 16a4 4 0 1 1 0-8 4 4 0 0 1 0 8zm6.406-11.845a1.44 1.44 0 1 0 0 2.881 1.44 1.44 0 0 0 0-2.881z"/>
                </svg>
              </div>
              <span>Instagram</span>
            </a>

            {/* Facebook */}
            <a href="https://www.facebook.com/profile.php?id=61581368698905" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon facebook">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#1877F2">
                  <path d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796v8.385C19.612 23.027 24 18.062 24 12.073z"/>
                </svg>
              </div>
              <span>{t('settingsPage.facebook', 'Facebook')}</span>
            </a>

            {/* Discord */}
            <a href="https://discord.gg/neXEXTcQpW" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon discord">
                <svg width="28" height="28" viewBox="0 0 127.14 96.36" fill="#5865F2">
                  <path d="M107.7,8.07A105.15,105.15,0,0,0,81.47,0a72.06,72.06,0,0,0-3.36,6.83A97.68,97.68,0,0,0,49,6.83,72.37,72.37,0,0,0,45.64,0,105.89,105.89,0,0,0,19.39,8.09C2.79,32.65-1.71,56.6.54,80.21h0A105.73,105.73,0,0,0,32.71,96.36,77.7,77.7,0,0,0,39.6,85.25a68.42,68.42,0,0,1-10.85-5.18c.91-.66,1.8-1.34,2.66-2a75.57,75.57,0,0,0,64.32,0c.87.71,1.76,1.39,2.66,2a68.68,68.68,0,0,1-10.87,5.19,77,77,0,0,0,6.89,11.1A105.25,105.25,0,0,0,126.6,80.22h0C129.24,52.84,122.09,29.11,107.7,8.07ZM42.45,65.69C36.18,65.69,31,60,31,53s5-12.74,11.43-12.74S54,46,53.89,53,48.84,65.69,42.45,65.69Zm42.24,0C78.41,65.69,73.25,60,73.25,53s5-12.74,11.44-12.74S96.23,46,96.12,53,91.08,65.69,84.69,65.69Z"/>
                </svg>
              </div>
              <span>{t('settingsPage.discord', 'Discord')}</span>
            </a>

            {/* Reddit */}
            <a href="https://www.reddit.com/r/mythicmapsai/" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon reddit">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#FF4500">
                  <path d="M12 0C5.373 0 0 5.373 0 12c0 3.314 1.343 6.314 3.515 8.485l-2.286 2.286C.775 23.225 1.097 24 1.738 24H12c6.627 0 12-5.373 12-12S18.627 0 12 0Zm4.388 3.199c1.104 0 1.999.895 1.999 1.999 0 1.105-.895 2-1.999 2-.946 0-1.739-.657-1.947-1.539v.002c-1.147.162-2.032 1.15-2.032 2.341v.007c1.776.067 3.4.567 4.686 1.363.473-.363 1.064-.58 1.707-.58 1.547 0 2.802 1.254 2.802 2.802 0 1.117-.655 2.081-1.601 2.531-.088 3.256-3.637 5.876-7.997 5.876-4.361 0-7.905-2.617-7.998-5.87-.954-.447-1.614-1.415-1.614-2.538 0-1.548 1.255-2.802 2.803-2.802.645 0 1.239.218 1.712.585 1.275-.79 2.881-1.291 4.64-1.365v-.01c0-1.663 1.263-3.034 2.88-3.207.188-.911.993-1.595 1.959-1.595Zm-8.085 8.376c-.784 0-1.459.78-1.506 1.797-.047 1.016.64 1.429 1.426 1.429.786 0 1.371-.369 1.418-1.385.047-1.017-.553-1.841-1.338-1.841Zm7.406 0c-.786 0-1.385.824-1.338 1.841.047 1.017.634 1.385 1.418 1.385.785 0 1.473-.413 1.426-1.429-.046-1.017-.721-1.797-1.506-1.797Zm-3.703 4.013c-.974 0-1.907.048-2.77.135-.147.015-.241.168-.183.305.483 1.154 1.622 1.964 2.953 1.964 1.33 0 2.47-.81 2.953-1.964.057-.137-.037-.29-.184-.305-.863-.087-1.795-.135-2.769-.135Z"/>
                </svg>
              </div>
              <span>{t('settingsPage.reddit', 'Reddit')}</span>
            </a>

            {/* Strava Club */}
            <a href="https://www.strava.com/clubs/mythicmaps" target="_blank" rel="noopener noreferrer" className="social-card">
              <div className="social-icon strava">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="#FC5200">
                  <path d="M15.387 17.944l-2.089-4.116h-3.065L15.387 24l5.15-10.172h-3.066m-7.008-5.599l2.836 5.598h4.172L10.463 0l-7 13.828h4.169"/>
                </svg>
              </div>
              <span>{t('settingsPage.stravaClub', 'Strava Club')}</span>
            </a>

            {/* Email */}
            <a href="mailto:contact@mythicmaps.ai" className="social-card">
              <div className="social-icon email">
                <svg width="28" height="28" viewBox="0 0 24 24" fill="none" stroke="#c4956a" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  <rect x="2" y="4" width="20" height="16" rx="2"/>
                  <path d="m22 7-8.97 5.7a1.94 1.94 0 0 1-2.06 0L2 7"/>
                </svg>
              </div>
              <span>{t('settingsPage.email', 'Email')}</span>
            </a>
          </div>
        </div>

        {/* Section 6: Legal */}
        <div className="settings-section">
          <h2>{t('settingsPage.legal', 'Legal')}</h2>
          <p className="section-description">{t('settingsPage.legalDesc', 'View our policies and terms')}</p>

          <div className="preference-card">
            <div className="legal-links-list">
              <Link to="/privacy-policy" className="legal-link-item">
                <div className="legal-link-content">
                  <h4>🔐 {t('footer.privacyPolicy', 'Privacy Policy')}</h4>
                  <p>{t('settingsPage.privacyPolicyDesc', 'Learn how we protect and handle your data')}</p>
                </div>
                <div className="legal-link-arrow">→</div>
              </Link>
              
              <Link to="/cookie-policy" className="legal-link-item">
                <div className="legal-link-content">
                  <h4>🍪 {t('footer.cookiePolicy', 'Cookie Policy')}</h4>
                  <p>{t('settingsPage.cookiePolicyDesc', 'Understand how we use cookies and similar technologies')}</p>
                </div>
                <div className="legal-link-arrow">→</div>
              </Link>
              
              <Link to="/terms-of-service" className="legal-link-item">
                <div className="legal-link-content">
                  <h4>📄 {t('footer.termsOfService', 'Terms of Service')}</h4>
                  <p>{t('settingsPage.termsOfServiceDesc', 'Review the terms governing your use of Mythic Maps')}</p>
                </div>
                <div className="legal-link-arrow">→</div>
              </Link>
            </div>
          </div>
        </div>

        {/* Delete Account Modal */}
        {showDeleteModal && (
          <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
            <div className="modal-content delete-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{t('settingsPage.deleteAccountModal', 'Delete Account')}</h2>
              <div className="warning-banner">
                <svg width="24" height="24" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
                <p>{t('settingsPage.cannotBeUndone', 'This action cannot be undone!')}</p>
              </div>

              <div className="deletion-details">
                <h4>{t('settingsPage.whatWillBeDeleted', 'What will be deleted:')}</h4>
                <ul>
                  <li>{t('settingsPage.allGeneratedMaps', 'All your generated maps and images')}</li>
                  <li>{t('settingsPage.personalInfo', 'Your personal information and settings')}</li>
                  <li>{t('settingsPage.stravaConnection', 'Your Strava connection (if connected)')}</li>
                </ul>

                <h4>{t('settingsPage.whatWillBePreserved', 'What will be preserved:')}</h4>
                <ul>
                  <li>{t('settingsPage.creditUsageHistory', 'Your remaining credits (can be restored)')}</li>
                  <li>{t('settingsPage.monthlyLimit', 'Your monthly generation limit')}</li>
                  <li>{t('settingsPage.earnedRewards', 'Earned rewards from sharing')}</li>
                </ul>

                <p className="info-text">
                  {t('settingsPage.restorationNote', 'If you sign in again later, your credits and limits will be restored, but your maps will not.')}
                </p>
              </div>

              <div className="confirmation-input">
                <label>{t('settingsPage.typeDeleteConfirm', 'Type "DELETE" to confirm:')}</label>
                <input
                  type="text"
                  value={deleteConfirmText}
                  onChange={(e) => setDeleteConfirmText(e.target.value)}
                  placeholder={t('settingsPage.typeDeletePlaceholder', 'Type DELETE')}
                  disabled={isDeletingAccount}
                />
              </div>

              <div className="modal-actions">
                <button
                  onClick={() => {
                    setShowDeleteModal(false);
                    setDeleteConfirmText('');
                  }}
                  className="cancel-button"
                  disabled={isDeletingAccount}
                >
                  {t('settingsPage.cancel', 'Cancel')}
                </button>
                <button
                  onClick={handleDeleteAccount}
                  className="confirm-delete-button"
                  disabled={deleteConfirmText !== 'DELETE' || isDeletingAccount}
                >
                  {isDeletingAccount ? t('settingsPage.deleting', 'Deleting...') : t('settingsPage.deleteMyAccount', 'Delete My Account')}
                </button>
              </div>
            </div>
          </div>
        )}

        {/* Disconnect Strava Modal */}
        {showDisconnectModal && (
          <div
            className="modal-overlay"
            onClick={() => { if (!isDisconnectingStrava) setShowDisconnectModal(false); }}
          >
            <div className="modal-content disconnect-modal" onClick={(e) => e.stopPropagation()}>
              <h2>{t('settingsPage.disconnectStravaModalTitle', 'Disconnect Strava')}</h2>
              <p className="disconnect-modal-body">
                {t('settingsPage.disconnectStravaConfirm', 'Are you sure you want to disconnect your Strava account from Mythic Maps? Auto-generation will stop until you reconnect.')}
              </p>
              {disconnectError && (
                <p className="disconnect-modal-error">{disconnectError}</p>
              )}
              <div className="modal-actions">
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  className="cancel-button"
                  disabled={isDisconnectingStrava}
                >
                  {t('settingsPage.cancel', 'Cancel')}
                </button>
                <button
                  onClick={confirmStravaDisconnect}
                  className="confirm-delete-button"
                  disabled={isDisconnectingStrava}
                >
                  {isDisconnectingStrava
                    ? t('settingsPage.disconnecting', 'Disconnecting…')
                    : t('settingsPage.disconnectStrava', 'Disconnect Strava')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}

export default SettingsPage;
